import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import breakpoints from "_constants/breakpoints";

import { ReactComponent as ArrowDownSVG } from "assets/img/ArrowDown.svg";

import Pagination from "components/Pagination/Pagination";

import ClassificationListMobile from "./ClassificationListMobile.jsx";
import OwnerList from "./OwnerListMobile";
import EventListMobile from "./EventListMobile";

const Content = styled.div`
  min-width: 100%;
  max-width: 100%;
  background: #ffffff;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);
  width: 100%;
  height: 0;
  opacity: 0;
  transform: scaleY(0);
  transform-origin: 0 0;
  transition: 0.5s ease-in-out;
  ${({ isOpen }) =>
    isOpen &&
    css`
      height: 100%;
      opacity: 1;
      transform: scaleY(1);
      margin-bottom: 33px;
    `}
`;
const SectionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: #ffffff;
  border: 1px solid #a5aaaf;
  box-sizing: border-box;
  padding: 8px 15px 8px 20px;
  margin-bottom: 10px;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 36px;
  text-align: left;
  color: #03589c;
  transform-origin: 0 0;
  transition: 0.5s ease-in-out;
  ${({ isOpen }) =>
    isOpen &&
    css`
      margin-bottom: 24px;
    `}

  @media (max-width: ${breakpoints.mobileL}) {
    line-height: 24px;
  }
`;
const Container = styled.div`
  background-color: #ffffff;
  width: 100%;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);

  &:last-child {
    margin-bottom: 0px;
  }
`;
const Result = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
const Group = styled.div`
  display: flex;
  min-width: 100%;
  padding: 16px 0;

  &:nth-child(2n + 1) {
    background-color: #fafaff;
  }

  @media (max-width: ${breakpoints.tablet}) {
    flex-direction: column;
  }

  @media (max-width: ${breakpoints.mobileL}) {
    padding: 16px 0;
  }
`;
const Label = styled.div`
  min-width: 50%;
  width: 50%;
  padding-right: 20px;
  font-weight: normal;
  font-size: 12px;
  text-transform: uppercase;
  color: #7b848d;

  @media (max-width: ${breakpoints.tablet}) {
    width: 100%;
    padding-right: 0;
  }
`;
const Data = styled(Label)`
  font-weight: bold;
  text-transform: none;
  word-break: break-all;

  @media (max-width: ${breakpoints.mobileL}) {
    align-items: center;
  }
`;
const EmptyMessage = styled.div`
  color: #7b848d;
  font-weight: bold;
  text-align: center;

  padding: 24px;
  @media (max-width: ${breakpoints.mobileL}) {
    padding: 24px 16px;
  }
`;

const initPages = { classificationsPage: 1, eventsPage: 1 };

function FederalSearchResultsMobile({ federalSearchResults }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [innerPages, setInnerPages] = useState(initPages);
  const [isOpen, setOpen] = useState(false);

  const contentRef = useRef(null);

  const result = federalSearchResults[currentPage - 1];

  const handleChangePage = (page) => {
    if (page !== currentPage) {
      setInnerPages(initPages);
      setCurrentPage(page);
      contentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <SectionButton id="federal-search-results" isOpen={isOpen} onClick={() => setOpen(!isOpen)}>
        Federal Search Results
        <ArrowDownSVG fill="#F37217" transform={isOpen ? "rotate(180)" : undefined} />
      </SectionButton>

      <Content ref={contentRef} isOpen={isOpen}>
        {result ? (
          <>
            <Group>
              <Pagination
                top
                currentPage={currentPage}
                totalPages={federalSearchResults.length}
                onClick={handleChangePage}
              />
            </Group>

            <Result>
              <Container>
                <Group>
                  <Label>Serial number</Label>
                  <Data>{result.serialNumber}</Data>
                </Group>

                <Group>
                  <Label>Registration number</Label>
                  <Data>{result.registrationNumber}</Data>
                </Group>

                <Group>
                  <Label>Mark Identification</Label>
                  <Data>{result.markIdentification}</Data>
                </Group>

                <Group>
                  <Label>Status code</Label>
                  <Data>{result.statusCode}</Data>
                </Group>

                <Group>
                  <Label>Status date</Label>
                  <Data>{result.statusDate}</Data>
                </Group>

                <Group>
                  <Label>Filing date</Label>
                  <Data>{result.filingDate}</Data>
                </Group>

                <Group>
                  <Label>Registration date</Label>
                  <Data>{result.registrationDate}</Data>
                </Group>

                <Group>
                  <Label>Attorney name</Label>
                  <Data>{result.attorneyName}</Data>
                </Group>

                <Group>
                  <Label>Mark drawing code</Label>
                  <Data>{result.markDrawingCode}</Data>
                </Group>
              </Container>

              <Container>
                <ClassificationListMobile
                  classifications={result.classification}
                  currentPage={innerPages.classificationsPage}
                  changePage={(page) => setInnerPages({ ...innerPages, classificationsPage: page })}
                />
              </Container>

              <Container>
                <OwnerList owners={result.owners} />
              </Container>

              <Container>
                <Group>
                  <Label>Correspondence name</Label>
                  <Data>{result.correspondenceName}</Data>
                </Group>

                <Group>
                  <Label>Correspondence address</Label>
                  <Data>{result.correspondenceAddress1}</Data>
                </Group>

                <Group>
                  <Label>Correspondence address 2</Label>
                  <Data>{result.correspondenceAddress2}</Data>
                </Group>
              </Container>

              <Container>
                <EventListMobile
                  events={result.events}
                  currentPage={innerPages.eventsPage}
                  changePage={(page) => setInnerPages({ ...innerPages, eventsPage: page })}
                />
              </Container>
            </Result>
          </>
        ) : (
          <EmptyMessage>No conflicting Federal search results found</EmptyMessage>
        )}
      </Content>
    </>
  );
}

FederalSearchResultsMobile.propTypes = {
  federalSearchResults: PropTypes.arrayOf(PropTypes.object),
};

export default FederalSearchResultsMobile;
